import { z } from 'zod'

/**
 * Stored organization values that are not free text.
 *
 * The settings form, the update route and the settings response all have to
 * agree on what a state, a phone number, a business name and a day count are.
 * Keeping those checks on the input alone left the response typed as any
 * string or any integer, so a row that could not have been saved still
 * type-checked as settings.
 */

/**
 * A two-letter state, already upper case. "TX", never "Texas" or "tx".
 *
 * The phantom property is how the type stays distinct from string without a
 * unique symbol. A symbol brand cannot be named in the declaration of a Zod
 * schema that lives in another file, so the settings contract would not build.
 */
export type LicenseState = string & {
  readonly __licenseState: 'LicenseState'
}

/** A phone number as the business writes it. "(806) 794-1300". */
export type OrganizationPhone = string & {
  readonly __organizationPhone: 'OrganizationPhone'
}

const LICENSE_STATE_PATTERN = /^[A-Z]{2}$/
const PHONE_PATTERN = /^[0-9()+.\-\s]{7,40}$/

export function isLicenseState(value: string): value is LicenseState {
  return LICENSE_STATE_PATTERN.test(value)
}

export function isOrganizationPhone(value: string): value is OrganizationPhone {
  return PHONE_PATTERN.test(value)
}

/** Turn a string the state check has already accepted into that state. */
export function toLicenseState(value: string): LicenseState {
  if (!isLicenseState(value)) {
    throw new Error('Enter a two-letter state, e.g. TX')
  }
  return value
}

/** Turn a string the phone check has already accepted into that phone. */
export function toOrganizationPhone(value: string): OrganizationPhone {
  if (!isOrganizationPhone(value)) {
    throw new Error('Enter a phone number, e.g. (806) 794-1300')
  }
  return value
}

export const licenseStateSchema = z
  .string()
  .refine(isLicenseState, 'Enter a two-letter state, e.g. TX')
  .transform(toLicenseState)

export const organizationPhoneSchema = z
  .string()
  .refine(isOrganizationPhone, 'Enter a phone number, e.g. (806) 794-1300')
  .transform(toOrganizationPhone)

function hasControlCharacter(value: string): boolean {
  return [...value].some((char) => {
    const code = char.codePointAt(0) ?? 0
    return code < 0x20 || code === 0x7f
  })
}

/**
 * The business name, as stored.
 *
 * Never blank, and never a line break: this string is the label in front of
 * the sending address on mail to clients, and an email header ends at a
 * newline.
 */
export const organizationNameSchema = z
  .string()
  .min(1, 'Enter the business name')
  .max(200)
  .refine(
    (value) => !hasControlCharacter(value),
    'The business name cannot contain line breaks or control characters',
  )

/** Days a price holds after sending. Null, on the row, means no expiry. */
export const proposalValidityDaysSchema = z
  .number()
  .int('Enter a whole number of days')
  .min(1, 'Enter at least 1 day')
  .max(365, 'Enter at most 365 days')

/** Days before a season starts that its reminders are due. */
export const seasonReminderLeadDaysSchema = z
  .number()
  .int('Enter a whole number of days')
  .min(1, 'Enter at least 1 day')
  .max(90, 'Enter at most 90 days')
